import { ArrowLeftRight, Ban, FileText, Undo2 } from 'lucide-react'
import { useState } from 'react'

import type { LedgerJournalEntryDetail, LedgerJournalLineRow } from '@/api/ledger-types'
import { ErrorSurface } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatBusinessDate, formatDateTime } from '@/lib/datetime'

import { LedgerAuditTrail } from './audit-trail-panel'
import { JOURNAL_STATUS_LABEL, POSTING_SOURCE_LABEL } from './constants'
import { useLedgerPermissions } from './permissions'
import { useJournal } from './queries'
import { ReverseJournalDialog } from './reverse-journal'

/**
 * One journal entry, with every line on it.
 *
 * **There is no edit here and there never will be.** A posted journal is immutable in the database
 * and immutable in the contract: no endpoint updates one, no endpoint deletes one, and the only
 * correction the ledger recognises is a reversal — a second entry that undoes the first, with both
 * left in the book and the reason recorded on the pair.
 *
 * That is stated on the screen rather than merely implemented, in the place somebody would look for
 * an edit button. An accountant who finds a wrong figure and no way to change it will otherwise
 * assume the control is missing or that their permissions are short, and go looking. Naming the
 * rule where the button would be is what turns an absence into an answer.
 */
export function JournalDetailPanel({
  journalPublicId,
  onReversalPosted,
}: {
  journalPublicId: string | null
  /** Told when a reversal completed, so a list beside this can move to it. */
  onReversalPosted?: (entryNumber: string | null) => void
}) {
  const permissions = useLedgerPermissions()
  const [reversing, setReversing] = useState(false)

  const query = useJournal(journalPublicId, permissions.canViewJournals)

  if (journalPublicId === null) {
    return (
      <Card>
        <CardContent>
          <p className="py-8 text-center text-sm text-muted">
            Choose an entry from the list to read it and its lines.
          </p>
        </CardContent>
      </Card>
    )
  }

  if (query.isPending) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (query.isError) {
    return (
      <ErrorSurface
        error={query.error}
        onRetry={() => void query.refetch()}
        title="That entry could not be read"
      />
    )
  }

  const entry = query.data
  const reversible = !entry.isReversed && entry.status === 'Posted'

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="min-w-0 space-y-1">
            <CardTitle className="flex flex-wrap items-center gap-2">
              <FileText className="size-4 text-muted" aria-hidden />
              <span className="font-mono">{entry.entryNumber}</span>
              <Badge tone={entry.status === 'Reversed' ? 'danger' : 'success'}>
                {JOURNAL_STATUS_LABEL[entry.status]}
              </Badge>
              {entry.isReversal ? <Badge tone="warning">Reversal</Badge> : null}
              {entry.isSystemGenerated ? <Badge tone="neutral">System</Badge> : null}
            </CardTitle>
            <p className="text-sm text-body">{entry.description}</p>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 lg:grid-cols-4">
            <Fact label="Business date" value={formatBusinessDate(entry.businessDate)} />
            <Fact
              label="Value date"
              value={formatBusinessDate(entry.valueDate)}
              note={
                entry.valueDate === entry.businessDate
                  ? undefined
                  : 'Backdated — the entry posted on one day and is effective from another.'
              }
            />
            <Fact label="Posted" value={formatDateTime(entry.postedAtUtc)} />
            <Fact label="Branch" value={`Branch ${entry.branchId}`} />
            <Fact label="Source" value={POSTING_SOURCE_LABEL[entry.source]} />
            <Fact
              label="Posted by"
              value={
                entry.postedByUserId === null
                  ? 'The system'
                  : (entry.postedByUsername ?? `user ${entry.postedByUserId}`)
              }
              note={
                entry.postedByUserId === null
                  ? 'No operator behind it — an accrual, a run, or a scheduled posting.'
                  : entry.postedByUsername === null
                    ? 'The account behind this identifier no longer exists in the directory.'
                    : 'Who countersigned, where anyone did, is in the audit trail below.'
              }
            />
            <Fact label="Lines" value={String(entry.lines.length)} />
            <Fact label="Currency" value={entry.currencyCode} />
          </dl>

          <ReversalRelationship entry={entry} />

          {/*
            Where an edit button would be. It says the rule instead, because the rule is the answer
            to the question somebody is asking when they look here.
          */}
          <Alert tone="info">
            <Ban aria-hidden />
            <div className="space-y-2">
              <AlertTitle>A posted entry is never edited</AlertTitle>
              <AlertDescription>
                Nothing changes this entry — not its lines, not its description, not its dates. The
                ledger&apos;s only correction is a reversal: a second entry that undoes this one,
                with both left in the book and the reason recorded against the pair. If this posting
                is wrong, reverse it and post a fresh one.
                {entry.isReversed
                  ? ' This entry has already been reversed, so there is nothing further to do to it.'
                  : ''}
              </AlertDescription>

              {permissions.canReverseJournal && reversible ? (
                <Button variant="secondary" size="sm" onClick={() => setReversing(true)}>
                  <Undo2 />
                  Reverse this entry
                </Button>
              ) : !permissions.canReverseJournal ? (
                <p className="text-xs text-muted">
                  Reversing needs <span className="font-mono">ledger.journal.reverse</span>, which
                  your role does not hold.
                </p>
              ) : null}
            </div>
          </Alert>
        </CardContent>
      </Card>

      <JournalLines entry={entry} />

      <Card>
        <CardHeader>
          <CardTitle>Who raised it</CardTitle>
        </CardHeader>
        <CardContent>
          <LedgerAuditTrail
            entityType="JournalEntry"
            entityId={entry.entryNumber}
            canRead={permissions.canReadAudit}
            emptyHint="Nothing is recorded under this entry number. A posting made before auditing was in place carries no entry — the journal above is still the record of what moved."
          />
        </CardContent>
      </Card>

      <ReverseJournalDialog
        entry={entry}
        open={reversing}
        onOpenChange={setReversing}
        onPosted={onReversalPosted}
      />
    </div>
  )
}

function Fact({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</dt>
      <dd className="mt-0.5 text-sm text-heading">
        <span className="numeric tabular-nums">{value}</span>
      </dd>
      {note ? <p className="mt-0.5 text-2xs text-muted">{note}</p> : null}
    </div>
  )
}

/**
 * The reversal pair, when there is one.
 *
 * Both directions are shown as the public identifier the contract carries, and the identifier is
 * shown rather than a link: the journal detail is addressed by UUID and the route tree does not
 * carry a parameterised journal path, so a link would be a promise this screen cannot keep. The
 * identifier can be pasted into the list's reference filter, which is what actually finds it.
 */
function ReversalRelationship({ entry }: { entry: LedgerJournalEntryDetail }) {
  if (
    entry.reversalOfEntryPublicId === null &&
    entry.reversedByEntryPublicId === null &&
    entry.reversalReason === null
  ) {
    return null
  }

  return (
    <Alert tone={entry.isReversed ? 'warning' : 'info'}>
      <ArrowLeftRight aria-hidden />
      <div className="space-y-1">
        <AlertTitle>
          {entry.isReversal ? 'This entry reverses another' : 'This entry has been reversed'}
        </AlertTitle>
        <AlertDescription>
          {entry.reversalOfEntryPublicId ? (
            <p>
              It undoes{' '}
              <span className="font-mono text-xs">{entry.reversalOfEntryPublicId}</span>.
            </p>
          ) : null}
          {entry.reversedByEntryPublicId ? (
            <p>
              It was undone by{' '}
              <span className="font-mono text-xs">{entry.reversedByEntryPublicId}</span>. Both entries
              stay in the book — that pair is the record.
            </p>
          ) : null}
          {entry.reversalReason ? <p className="mt-1">Reason given: {entry.reversalReason}</p> : null}
        </AlertDescription>
      </div>
    </Alert>
  )
}

/**
 * The lines.
 *
 * Every amount here is an integer count of pesewas rendered by {@link MoneyDisplay}. The totals row
 * shows the entry's own `totalDebitMinorUnits` and `totalCreditMinorUnits` as the server sent them
 * rather than a sum computed in the browser: a client-side total that disagreed with the server's
 * would leave nobody able to say which of the two was the ledger.
 *
 * The table is not the shared `DataTable`: these lines are not paged, not sortable and not
 * navigable, and they belong to the entry above them rather than being a list of their own.
 */
function JournalLines({ entry }: { entry: LedgerJournalEntryDetail }) {
  const balanced = entry.totalDebitMinorUnits === entry.totalCreditMinorUnits

  return (
    <Card>
      <CardHeader>
        <CardTitle>Lines</CardTitle>
        <Badge tone={balanced ? 'success' : 'danger'}>
          {balanced ? 'Debits equal credits' : 'Does not balance'}
        </Badge>
      </CardHeader>

      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <tr>
                <TableHead scope="col" className="w-12">
                  #
                </TableHead>
                <TableHead scope="col">Account</TableHead>
                <TableHead scope="col" className="w-36 text-right">
                  Debit
                </TableHead>
                <TableHead scope="col" className="w-36 text-right">
                  Credit
                </TableHead>
                <TableHead scope="col">Narration</TableHead>
              </tr>
            </TableHeader>
            <TableBody>
              {entry.lines.map((line) => (
                <TableRow key={line.lineNumber}>
                  <TableCell className="numeric tabular-nums">{line.lineNumber}</TableCell>
                  <TableCell>
                    <span className="font-mono text-xs text-heading">{line.glAccountCode}</span>
                    <span className="ml-2 text-xs text-body">{line.glAccountName}</span>
                    <LineDimensions line={line} />
                  </TableCell>
                  <TableCell className="text-right">
                    {line.debitMinorUnits === 0 ? (
                      <span className="text-muted">—</span>
                    ) : (
                      <MoneyDisplay value={line.debitMinorUnits} currency={line.currencyCode} symbol={false} />
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {line.creditMinorUnits === 0 ? (
                      <span className="text-muted">—</span>
                    ) : (
                      <MoneyDisplay
                        value={line.creditMinorUnits}
                        currency={line.currencyCode}
                        symbol={false}
                      />
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-body">{line.narration ?? '—'}</TableCell>
                </TableRow>
              ))}

              <TableRow className="bg-surface-muted hover:bg-surface-muted">
                <TableCell />
                <TableCell className="text-xs font-semibold text-heading">
                  Total ({entry.currencyCode})
                </TableCell>
                <TableCell className="text-right">
                  <MoneyDisplay
                    value={entry.totalDebitMinorUnits}
                    currency={entry.currencyCode}
                    symbol={false}
                    emphasis="strong"
                  />
                </TableCell>
                <TableCell className="text-right">
                  <MoneyDisplay
                    value={entry.totalCreditMinorUnits}
                    currency={entry.currencyCode}
                    symbol={false}
                    emphasis="strong"
                  />
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * The subsidiary dimensions a line carries, and only the ones it carries.
 *
 * Every one of them is nullable and most lines have none. A grid of six labelled dashes would push
 * the account name — the thing being read — off the useful part of the row.
 */
function LineDimensions({ line }: { line: LedgerJournalLineRow }) {
  const dimensions: string[] = []

  if (line.customerId !== null) dimensions.push(`customer ${line.customerId}`)
  if (line.savingsAccountId !== null) dimensions.push(`savings ${line.savingsAccountId}`)
  if (line.loanCycleId !== null) dimensions.push(`cycle ${line.loanCycleId}`)
  if (line.tillId !== null) dimensions.push(`till ${line.tillId}`)
  if (line.loanProductId !== null) dimensions.push(`product ${line.loanProductId}`)
  if (line.loanOfficerUserId !== null) dimensions.push(`officer ${line.loanOfficerUserId}`)

  if (dimensions.length === 0) {
    return null
  }

  return <p className="mt-0.5 font-mono text-2xs text-muted">{dimensions.join(' · ')}</p>
}
